import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputSwitch } from 'primereact/inputswitch';
import { Slider } from 'primereact/slider';
import { ColorPicker } from 'primereact/colorpicker';
import { Dropdown } from 'primereact/dropdown';
import ToolIllustrations from './ToolIllustrations';

/**
 * Helper function to render tab content
 */
const renderTabContent = (activeTab, toolsState, updateTool, toggleTool, setAreaColor, setAreaOpacity, showToolTutorial, areas, backgroundPatterns, isDarkMode, t) => {
    // View Tab
    if (activeTab === 'view') {
        return (
            <div className="space-y-4">
                <ToolSection
                    title={t('layouts.designer_tool_area_numbers')}
                    icon="bx-hash"
                    enabled={toolsState.areaNumbers.visible}
                    onToggle={() => updateTool('areaNumbers', { visible: !toolsState.areaNumbers.visible })}
                    onHelp={() => showToolTutorial('areaNumbers')}
                    isDarkMode={isDarkMode}
                >
                    <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                         isDarkMode ? 'bg-indigo-900/20 border-indigo-900/50' : 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100'
                    }`}>
                        <div className="w-full h-48 flex items-center justify-center">
                            <div className="max-w-full max-h-full">
                                <ToolIllustrations.areaNumbers />
                            </div>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div className={`text-xs p-3 rounded-lg ${isDarkMode ? 'bg-slate-800 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                            {t('layouts.designer_area_numbers_desc')}
                        </div>
                        <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_number_position')}</label>
                            <Dropdown
                                value={toolsState.areaNumbers.position}
                                options={[
                                    { label: t('layouts.designer_pos_top'), value: 'top' },
                                    { label: t('layouts.designer_pos_center'), value: 'center' },
                                    { label: t('layouts.designer_pos_bottom'), value: 'bottom' }
                                ]}
                                onChange={(e) => updateTool('areaNumbers', { position: e.value })}
                                className={`w-full text-sm ${isDarkMode ? 'p-dropdown-dark' : ''}`}
                                pt={{
                                    root: { className: isDarkMode ? 'bg-slate-800 border-slate-700' : '' },
                                    input: { className: `text-xs py-2 ${isDarkMode ? 'text-gray-200' : ''}` },
                                    panel: { className: `text-xs ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}` },
                                    item: { className: isDarkMode ? 'text-gray-200 hover:bg-slate-700' : '' }
                                }}
                            />
                        </div>
                    </div>
                </ToolSection>

                <ToolSection
                    title={t('layouts.designer_tool_timeline')}
                    icon="bx-time"
                    enabled={toolsState.timeline.visible}
                    onToggle={() => updateTool('timeline', { visible: !toolsState.timeline.visible })}
                    onHelp={() => showToolTutorial('timeline')}
                    isDarkMode={isDarkMode}
                >
                    <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                        isDarkMode ? 'bg-indigo-900/20 border-indigo-900/50' : 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100'
                    }`}>
                        <div className="w-full h-48 flex items-center justify-center">
                            <div className="max-w-full max-h-full">
                                <ToolIllustrations.timeline />
                            </div>
                        </div>
                    </div>
                    <div className={`text-xs p-3 rounded-lg ${isDarkMode ? 'bg-slate-800 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                        {t('layouts.designer_timeline_desc')}
                    </div>
                </ToolSection>

                <ToolSection
                    title={t('layouts.designer_tool_outline_mode')}
                    icon="bx-square"
                    enabled={toolsState.outlineMode.enabled}
                    onToggle={() => toggleTool('outlineMode')}
                    onHelp={() => showToolTutorial('outlineMode')}
                    isDarkMode={isDarkMode}
                >
                    <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                        isDarkMode ? 'bg-indigo-900/20 border-indigo-900/50' : 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100'
                    }`}>
                        <div className="w-full h-48 flex items-center justify-center">
                            <div className="max-w-full max-h-full">
                                <ToolIllustrations.outlineMode />
                            </div>
                        </div>
                    </div>
                    <div className={`text-xs p-3 rounded-lg ${isDarkMode ? 'bg-slate-800 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                        {t('layouts.designer_outline_mode_desc')}
                    </div>
                </ToolSection>

                <ToolSection
                    title={t('layouts.designer_tool_background_pattern')}
                    icon="bx-grid"
                    onHelp={() => showToolTutorial('backgroundPattern')}
                    isDarkMode={isDarkMode}
                >
                    <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                        isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gradient-to-br from-gray-50 to-slate-50 border-gray-100'
                    }`}>
                        <div className="w-full h-48 flex items-center justify-center">
                            <div className="max-w-full max-h-full">
                                <ToolIllustrations.backgroundPattern />
                            </div>
                        </div>
                    </div>
                    <Dropdown
                        value={toolsState.backgroundPattern}
                        options={backgroundPatterns}
                        onChange={(e) => updateTool('backgroundPattern', e.value)}
                        className={`w-full text-sm ${isDarkMode ? 'p-dropdown-dark' : ''}`}
                        pt={{
                            root: { className: isDarkMode ? 'bg-slate-800 border-slate-700' : '' },
                            input: { className: `text-xs py-2 ${isDarkMode ? 'text-gray-200' : ''}` },
                            panel: { className: `text-xs ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}` },
                            item: { className: isDarkMode ? 'text-gray-200 hover:bg-slate-700' : '' }
                        }}
                    />
                </ToolSection>

                <ToolSection
                    title={t('layouts.designer_tool_focus_mode')}
                    icon="bx-bullseye"
                    enabled={toolsState?.focusMode?.enabled}
                    onToggle={() => toggleTool('focusMode')}
                    onHelp={() => showToolTutorial('focusMode')}
                    isDarkMode={isDarkMode}
                >
                    <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                        isDarkMode ? 'bg-blue-900/20 border-blue-900/50' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100'
                    }`}>
                         <div className="w-full h-48 flex items-center justify-center">
                            <div className="max-w-full max-h-full">
                                <ToolIllustrations.focusMode />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3">
                         <div className={`text-xs p-3 rounded-lg ${isDarkMode ? 'bg-slate-800 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                            {t('layouts.designer_focus_mode_desc')}
                        </div>
                        <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_visibility_pct', { count: ((toolsState?.focusMode?.opacity || 0.3) * 100).toFixed(0) })}</label>
                            <Slider
                                value={(toolsState?.focusMode?.opacity || 0.3) * 100}
                                onChange={(e) => updateTool('focusMode', { opacity: e.value / 100 })}
                                min={5}
                                max={100}
                                className="w-full"
                            />
                        </div>
                    </div>
                </ToolSection>

                <ToolSection
                    title={t('layouts.designer_tool_contrast_check')}
                    icon="bx-adjust"
                    enabled={toolsState?.grayscale?.enabled}
                    onToggle={() => toggleTool('grayscale')}
                    onHelp={() => showToolTutorial('grayscale')}
                    isDarkMode={isDarkMode}
                >
                   <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                        isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gradient-to-br from-gray-100 to-slate-200 border-gray-300'
                   }`}>
                         <div className="w-full h-48 flex items-center justify-center">
                            <div className="max-w-full max-h-full">
                                <ToolIllustrations.grayscale />
                            </div>
                        </div>
                    </div>
                    <div className={`text-xs p-3 rounded-lg ${isDarkMode ? 'bg-slate-800 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                        {t('layouts.designer_contrast_check_desc')}
                    </div>
                </ToolSection>
            </div>
        );
    }

    // Grid Tab
    if (activeTab === 'grid') {
        return (
            <div className="space-y-4">
                <ToolSection
                    title={t('layouts.designer_tool_show_grid')}
                    icon="bx-grid-alt"
                    enabled={toolsState?.grid?.enabled}
                    onToggle={() => toggleTool('grid')}
                    onHelp={() => showToolTutorial('grid')}
                    isDarkMode={isDarkMode}
                >
                    <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                         isDarkMode ? 'bg-indigo-900/20 border-indigo-900/50' : 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100'
                    }`}>
                        <div className="w-full h-48 flex items-center justify-center">
                            <div className="max-w-full max-h-full">
                                <ToolIllustrations.grid />
                            </div>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_grid_size_px', { count: toolsState?.grid?.size })}</label>
                            <Slider
                                value={toolsState?.grid?.size}
                                onChange={(e) => updateTool('grid', { size: e.value })}
                                min={10}
                                max={50}
                                step={5}
                                className="w-full"
                            />
                        </div>
                        <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_grid_color')}</label>
                            <ColorPicker
                                value={toolsState?.grid?.color?.replace('#', '')}
                                onChange={(e) => updateTool('grid', { color: `#${e.value}` })}
                                format="hex"
                                className="w-full"
                            />
                        </div>
                        <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_opacity_pct', { count: Math.round((toolsState?.grid?.opacity || 0.2) * 100) })}</label>
                            <Slider
                                value={toolsState?.grid?.opacity}
                                onChange={(e) => updateTool('grid', { opacity: e.value })}
                                min={0.1}
                                max={1}
                                step={0.1}
                                className="w-full"
                            />
                        </div>
                    </div>
                </ToolSection>

                <ToolSection
                    title={t('layouts.designer_tool_snap_to_grid')}
                    icon="bx-magnet"
                    enabled={toolsState?.grid?.snapToGrid}
                    onToggle={() => updateTool('grid', { snapToGrid: !toolsState?.grid?.snapToGrid })}
                    onHelp={() => showToolTutorial('snapToGrid')}
                    isDarkMode={isDarkMode}
                >
                    <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                        isDarkMode ? 'bg-amber-900/20 border-amber-900/50' : 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100'
                    }`}>
                        <div className="w-full h-48 flex items-center justify-center">
                            <div className="max-w-full max-h-full">
                                <ToolIllustrations.snapToGrid />
                            </div>
                        </div>
                    </div>
                    <div className={`text-xs p-3 rounded-lg border ${isDarkMode ? 'bg-amber-900/10 border-amber-800/50 text-amber-500' : 'bg-amber-50 border-amber-100 text-gray-600'}`}>
                        <i className={`bx bx-info-circle mr-1 ${isDarkMode ? 'text-amber-500' : 'text-amber-600'}`}></i>
                        {t('layouts.designer_snap_to_grid_desc')}
                    </div>
                </ToolSection>

                <ToolSection
                    title={t('layouts.designer_tool_magnet_snapping')}
                    icon="bx-magnet"
                    enabled={toolsState?.magnet?.enabled}
                    onToggle={() => toggleTool('magnet')}
                    onHelp={() => showToolTutorial('magnet')}
                    isDarkMode={isDarkMode}
                >
                     <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                        isDarkMode ? 'bg-indigo-900/20 border-indigo-900/50' : 'bg-gradient-to-br from-indigo-50 to-pink-50 border-indigo-100'
                     }`}>
                          <div className="w-full h-48 flex items-center justify-center">
                             <div className="max-w-full max-h-full">
                                 <ToolIllustrations.magnet />
                             </div>
                         </div>
                    </div>
                    <div className="space-y-3">
                        <div className={`text-xs p-3 rounded-lg ${isDarkMode ? 'bg-slate-800 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                            {t('layouts.designer_magnet_desc')}
                        </div>
                         <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_snap_distance_px', { count: toolsState?.magnet?.snapDistance })}</label>
                            <Slider
                                value={toolsState?.magnet?.snapDistance}
                                onChange={(e) => updateTool('magnet', { snapDistance: e.value })}
                                min={5}
                                max={50}
                                className="w-full"
                            />
                        </div>
                    </div>
                </ToolSection>
            </div>
        );
    }

    // Measure Tab
    if (activeTab === 'measure') {
        return (
            <div className="space-y-4">
                <ToolSection
                    title={t('layouts.designer_tool_crosshair')}
                    icon="bx-crosshair"
                    enabled={toolsState?.crosshair?.enabled}
                    onToggle={() => toggleTool('crosshair')}
                    onHelp={() => showToolTutorial('crosshair')}
                    isDarkMode={isDarkMode}
                >
                    <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                         isDarkMode ? 'bg-red-900/20 border-red-900/50' : 'bg-gradient-to-br from-red-50 to-orange-50 border-red-100'
                    }`}>
                         <div className="w-full h-48 flex items-center justify-center">
                            <div className="max-w-full max-h-full">
                                <ToolIllustrations.crosshair />
                            </div>
                        </div>
                    </div>
                    <div className={`text-xs p-3 rounded-lg ${isDarkMode ? 'bg-slate-800 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                        {t('layouts.designer_crosshair_desc')}
                    </div>
                </ToolSection>

                <ToolSection
                    title={t('layouts.designer_tool_rulers')}
                    icon="bx-ruler"
                    enabled={toolsState.rulers.enabled}
                    onToggle={() => toggleTool('rulers')}
                    onHelp={() => showToolTutorial('rulers')}
                    isDarkMode={isDarkMode}
                >
                    <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                         isDarkMode ? 'bg-indigo-900/20 border-indigo-900/50' : 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100'
                    }`}>
                        <div className="w-full h-48 flex items-center justify-center">
                            <div className="max-w-full max-h-full">
                                <ToolIllustrations.rulers />
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_ruler_color')}</label>
                        <ColorPicker
                            value={toolsState.rulers.color.replace('#', '')}
                            onChange={(e) => updateTool('rulers', { color: `#${e.value}` })}
                            format="hex"
                            className="w-full"
                        />
                    </div>
                </ToolSection>

                <ToolSection
                    title={t('layouts.designer_tool_alignment_guides')}
                    icon="bx-target-lock"
                    enabled={toolsState.alignmentGuides.enabled}
                    onToggle={() => toggleTool('alignmentGuides')}
                    onHelp={() => showToolTutorial('alignmentGuides')}
                    isDarkMode={isDarkMode}
                >
                    <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                         isDarkMode ? 'bg-purple-900/20 border-purple-900/50' : 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-100'
                    }`}>
                        <div className="w-full h-48 flex items-center justify-center">
                            <div className="max-w-full max-h-full">
                                <ToolIllustrations.alignmentGuides />
                            </div>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_guide_color')}</label>
                            <ColorPicker
                                value={toolsState.alignmentGuides.color.replace('#', '')}
                                onChange={(e) => updateTool('alignmentGuides', { color: `#${e.value}` })}
                                format="hex"
                                className="w-full"
                            />
                        </div>
                    </div>
                </ToolSection>

                <ToolSection
                    title={t('layouts.designer_tool_safe_zones')}
                    icon="bx-shield"
                    enabled={toolsState.safeZones.enabled}
                    onToggle={() => toggleTool('safeZones')}
                    onHelp={() => showToolTutorial('safeZones')}
                    isDarkMode={isDarkMode}
                >
                    <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                         isDarkMode ? 'bg-red-900/20 border-red-900/50' : 'bg-gradient-to-br from-red-50 to-orange-50 border-red-100'
                    }`}>
                        <div className="w-full h-48 flex items-center justify-center">
                            <div className="max-w-full max-h-full">
                                <ToolIllustrations.safeZones />
                            </div>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_margin_pct', { count: toolsState.safeZones.margin })}</label>
                            <Slider
                                value={toolsState.safeZones.margin}
                                onChange={(e) => updateTool('safeZones', { margin: e.value })}
                                min={0}
                                max={15}
                                step={1}
                                className="w-full"
                            />
                        </div>
                        <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_zone_color')}</label>
                            <ColorPicker
                                value={toolsState.safeZones.color.replace('#', '')}
                                onChange={(e) => updateTool('safeZones', { color: `#${e.value}` })}
                                format="hex"
                                className="w-full"
                            />
                        </div>
                    </div>
                </ToolSection>
            </div>
        );
    }

    // Colors Tab
    if (activeTab === 'colors') {
        return (
            <div className="space-y-4">
                <div className={`p-4 rounded-xl border transition-colors ${
                    isDarkMode ? 'bg-indigo-900/20 border-indigo-900/50' : 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-100'
                }`}>
                    <div className="flex items-center gap-2 mb-3">
                        <i className={`bx bx-palette text-lg ${isDarkMode ? 'text-indigo-400' : 'text-purple-600'}`}></i>
                        <h4 className={`text-sm font-bold m-0 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{t('layouts.designer_area_colors')}</h4>
                    </div>
                    <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_area_colors_desc')}</p>

                    {areas.length === 0 ? (
                        <div className={`text-xs text-center py-4 rounded-lg ${isDarkMode ? 'bg-slate-800 text-gray-500' : 'bg-white text-gray-500'}`}>
                            {t('layouts.designer_no_areas_available')}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {areas.map((area, index) => (
                                <div key={area.id} className={`p-3 rounded-lg border transition-colors ${
                                    isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-indigo-700' : 'bg-white border-gray-100 hover:border-purple-200'
                                }`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                            {area.name || `${t('layouts.designer_area_fallback')} ${index + 1}`}
                                        </span>
                                        <button
                                            onClick={() => showToolTutorial('colorPicker')}
                                            className={`transition-colors ${isDarkMode ? 'text-slate-500 hover:text-indigo-400' : 'text-gray-400 hover:text-purple-600'}`}
                                        >
                                            <i className="bx bx-help-circle text-sm"></i>
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <ColorPicker
                                            value={(toolsState.areaColors[area.id] || '#8b5cf6').replace('#', '')}
                                            onChange={(e) => setAreaColor(area.id, `#${e.value}`)}
                                            format="hex"
                                        />
                                        <div className="flex-1">
                                            <label className={`text-[10px] mb-1 block ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t('layouts.designer_opacity_pct', { count: Math.round((toolsState.areaOpacity[area.id] || 0.4) * 100) })}</label>
                                            <Slider
                                                value={toolsState.areaOpacity[area.id] || 0.4}
                                                onChange={(e) => setAreaOpacity(area.id, e.value)}
                                                min={0.1}
                                                max={1}
                                                step={0.1}
                                                className="w-full"
                                                pt={{
                                                    root: { className: 'h-1' }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }



    // Style Tab (Borders)
    if (activeTab === 'style') {
        const borderStyles = [
            { label: t('layouts.designer_border_solid'), value: 'solid' },
            { label: t('layouts.designer_border_dashed'), value: 'dashed' },
            { label: t('layouts.designer_border_dotted'), value: 'dotted' }
        ];

        return (
            <div className="space-y-4">
                <ToolSection
                    title={t('layouts.designer_tool_area_borders')}
                    icon="bx-border-all"
                    enabled={true} // Always enabled for editing
                    onToggle={() => {}} // No toggle needed
                    onHelp={() => showToolTutorial('areaBorder')}
                    isDarkMode={isDarkMode}
                >
                    <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                         isDarkMode ? 'bg-blue-900/20 border-blue-900/50' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100'
                    }`}>
                         <div className="w-full h-48 flex items-center justify-center">
                            <div className="max-w-full max-h-full">
                                <ToolIllustrations.areaBorder />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3">
                         <div className={`text-xs p-3 rounded-lg ${isDarkMode ? 'bg-slate-800 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                            {t('layouts.designer_area_borders_desc')}
                        </div>

                        <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_border_style')}</label>
                            <Dropdown
                                value={toolsState?.areaBorder?.style || 'solid'}
                                options={borderStyles}
                                onChange={(e) => updateTool('areaBorder', { style: e.value })}
                                className={`w-full text-sm ${isDarkMode ? 'p-dropdown-dark' : ''}`}
                                pt={{
                                    root: { className: isDarkMode ? 'bg-slate-800 border-slate-700' : '' },
                                    input: { className: `text-xs py-2 ${isDarkMode ? 'text-gray-200' : ''}` },
                                    panel: { className: `text-xs ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}` },
                                    item: { className: isDarkMode ? 'text-gray-200 hover:bg-slate-700' : '' }
                                }}
                            />
                        </div>

                        <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_border_width_px', { count: toolsState?.areaBorder?.width || 1 })}</label>
                            <Slider
                                value={toolsState?.areaBorder?.width || 1}
                                onChange={(e) => updateTool('areaBorder', { width: e.value })}
                                min={0}
                                max={10}
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_border_color')}</label>
                            <ColorPicker
                                value={(toolsState?.areaBorder?.color || '#3b82f6').replace('#', '')}
                                onChange={(e) => updateTool('areaBorder', { color: `#${e.value}` })}
                                format="hex"
                                className="w-full"
                            />
                        </div>
                    </div>
                </ToolSection>

                <ToolSection
                    title={t('layouts.designer_tool_resize_handles')}
                    icon="bx-move"
                    enabled={toolsState?.resizeHandles?.visible}
                    onToggle={() => updateTool('resizeHandles', { visible: !toolsState?.resizeHandles?.visible })}
                    onHelp={() => showToolTutorial('resizeHandles')}
                    isDarkMode={isDarkMode}
                >
                    <div className={`mb-3 p-4 rounded-lg border flex items-center justify-center overflow-hidden ${
                         isDarkMode ? 'bg-cyan-900/20 border-cyan-900/50' : 'bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-100'
                    }`}>
                        <div className="w-full h-48 flex items-center justify-center">
                            <div className="max-w-full max-h-full">
                                <ToolIllustrations.resizeHandles />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className={`text-xs p-3 rounded-lg ${isDarkMode ? 'bg-slate-800 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                            {t('layouts.designer_resize_handles_desc')}
                        </div>

                         <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_handle_shape')}</label>
                            <Dropdown
                                value={toolsState?.resizeHandles?.shape || 'square'}
                                options={[
                                    { label: t('layouts.designer_shape_square'), value: 'square' },
                                    { label: t('layouts.designer_shape_circle'), value: 'circle' },
                                    { label: t('layouts.designer_shape_rounded'), value: 'rounded' },
                                    { label: t('layouts.designer_shape_pill'), value: 'pill' }
                                ]}
                                onChange={(e) => updateTool('resizeHandles', { shape: e.value })}
                                className={`w-full text-sm ${isDarkMode ? 'p-dropdown-dark' : ''}`}
                                pt={{
                                    root: { className: isDarkMode ? 'bg-slate-800 border-slate-700' : '' },
                                    input: { className: `text-xs py-2 ${isDarkMode ? 'text-gray-200' : ''}` },
                                    panel: { className: `text-xs ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}` },
                                    item: { className: isDarkMode ? 'text-gray-200 hover:bg-slate-700' : '' }
                                }}
                            />
                        </div>

                        <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_handle_size_px', { count: toolsState?.resizeHandles?.size || 8 })}</label>
                            <Slider
                                value={toolsState?.resizeHandles?.size || 8}
                                onChange={(e) => updateTool('resizeHandles', { size: e.value })}
                                min={6}
                                max={16}
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label className={`text-xs font-semibold mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('layouts.designer_handle_color')}</label>
                            <ColorPicker
                                value={(toolsState?.resizeHandles?.color || '#ffffff').replace('#', '')}
                                onChange={(e) => updateTool('resizeHandles', { color: `#${e.value}` })}
                                format="hex"
                                className="w-full"
                            />
                        </div>
                    </div>
                </ToolSection>
            </div>
        );
    }

    return null;
};

/**
 * Design Tools Panel
 * Main sidebar panel containing all design tools organized in tabs
 */
const DesignToolsPanel = ({ 
    isOpen, 
    onClose, 
    toolsState, 
    updateTool, 
    toggleTool,
    setAreaColor,
    setAreaOpacity,
    showToolTutorial,
    areas = [],
    embedded = false,
    activeTab: propActiveTab,
    setActiveTab: propSetActiveTab
}) => {
    const { t } = useTranslation();
    const [localActiveTab, setLocalActiveTab] = useState('view');
    const activeTab = propActiveTab || localActiveTab;
    const setActiveTab = propSetActiveTab || setLocalActiveTab;

    const tabs = [
        { id: 'view', label: t('layouts.designer_tab_view'), icon: 'bx-show' },
        { id: 'grid', label: t('layouts.designer_tab_grid'), icon: 'bx-grid-alt' },
        { id: 'measure', label: t('layouts.designer_tab_measure'), icon: 'bx-ruler' },
        { id: 'style', label: t('layouts.designer_tab_borders'), icon: 'bx-border-all' },
        { id: 'colors', label: t('layouts.designer_tab_colors'), icon: 'bx-palette' }
    ];

    const backgroundPatterns = [
        { label: t('layouts.designer_bg_none'), value: 'none' },
        { label: t('layouts.designer_bg_dots'), value: 'dots' },
        { label: t('layouts.designer_bg_lines'), value: 'lines' },
        { label: t('layouts.designer_bg_checkerboard'), value: 'checkerboard' }
    ];

    const isDarkMode = toolsState?.darkMode;

    if (!isOpen && !embedded) return null;

    // Embedded mode (inside existing sidebar)
    if (embedded) {
        return (
            <div className="space-y-4">
                {/* Tabs */}
                <div className={`flex gap-1 p-1 rounded-lg border transition-colors ${
                    isDarkMode ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'
                }`}>
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 flex flex-col items-center gap-1 py-2 px-2 rounded-md transition-all text-xs font-medium ${
                                activeTab === tab.id
                                    ? (isDarkMode ? 'bg-indigo-900/50 text-indigo-400 shadow-md border border-indigo-800' : 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-md')
                                    : (isDarkMode ? 'text-slate-500 hover:bg-slate-900 hover:text-slate-300' : 'text-gray-600 hover:bg-gray-100')
                            }`}
                        >
                            <i className={`bx ${tab.icon} text-base`}></i>
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="space-y-4">
                    {renderTabContent(activeTab, toolsState, updateTool, toggleTool, setAreaColor, setAreaOpacity, showToolTutorial, areas, backgroundPatterns, isDarkMode, t)}
                </div>
            </div>
        );
    }

    // Original standalone panel mode
    return (
        <div className="fixed right-0 top-14 bottom-0 w-80 bg-white border-l border-gray-200 shadow-2xl z-50 flex flex-col animate-slide-in-right">
            {/* Header */}
            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50 shrink-0">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                            <i className="bx bx-wrench text-white text-sm"></i>
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 text-sm m-0">{t('layouts.designer_design_tools')}</h3>
                            <p className="text-xs text-gray-500 m-0">{t('layouts.designer_layout_helpers')}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/80 text-gray-500 hover:text-gray-700 transition-colors"
                        title={t('layouts.designer_close_tools_panel')}
                    >
                        <i className="bx bx-x text-xl"></i>
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 bg-white p-1 rounded-lg border border-gray-200">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 flex flex-col items-center gap-1 py-2 px-2 rounded-md transition-all text-xs font-medium ${
                                activeTab === tab.id
                                    ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-md'
                                    : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            <i className={`bx ${tab.icon} text-base`}></i>
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                {/* View Tab */}
                {activeTab === 'view' && (
                    <div className="space-y-4">
                        <ToolSection
                            title={t('layouts.designer_area_numbers')}
                            icon="bx-hash"
                            enabled={toolsState.areaNumbers.visible}
                            onToggle={() => updateTool('areaNumbers', { visible: !toolsState.areaNumbers.visible })}
                            onHelp={() => showToolTutorial('areaNumbers')}
                        >
                            <div className="text-xs text-gray-600 bg-gray-50 p-3 rounded-lg">
                                {t('layouts.designer_area_numbers_desc')}
                            </div>
                        </ToolSection>

                        <ToolSection
                            title={t('layouts.designer_content_timeline')}
                            icon="bx-time"
                            enabled={toolsState.timeline.visible}
                            onToggle={() => updateTool('timeline', { visible: !toolsState.timeline.visible })}
                            onHelp={() => showToolTutorial('timeline')}
                        >
                            <div className="text-xs text-gray-600 bg-gray-50 p-3 rounded-lg">
                                {t('layouts.designer_content_timeline_desc')}
                            </div>
                        </ToolSection>

                        <ToolSection
                            title={t('layouts.designer_outline_mode')}
                            icon="bx-square"
                            enabled={toolsState.outlineMode.enabled}
                            onToggle={() => toggleTool('outlineMode')}
                            onHelp={() => showToolTutorial('outlineMode')}
                        >
                            <div className="text-xs text-gray-600 bg-gray-50 p-3 rounded-lg">
                                {t('layouts.designer_outline_mode_desc')}
                            </div>
                        </ToolSection>

                        <ToolSection
                            title={t('layouts.designer_background_pattern')}
                            icon="bx-grid"
                        >
                            <Dropdown
                                value={toolsState.backgroundPattern}
                                options={backgroundPatterns}
                                onChange={(e) => updateTool('backgroundPattern', e.value)}
                                className="w-full text-sm"
                                pt={{
                                    input: { className: 'text-xs py-2' },
                                    panel: { className: 'text-xs' }
                                }}
                            />
                        </ToolSection>
                    </div>
                )}

                {/* Grid Tab */}
                {activeTab === 'grid' && (
                    <div className="space-y-4">
                        <ToolSection
                            title={t('layouts.designer_show_grid')}
                            icon="bx-grid-alt"
                            enabled={toolsState.grid.enabled}
                            onToggle={() => toggleTool('grid')}
                            onHelp={() => showToolTutorial('grid')}
                        >
                            <div className="space-y-3">
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 mb-2 block">{t('layouts.designer_grid_size', { count: toolsState.grid.size })}</label>
                                    <Slider
                                        value={toolsState.grid.size}
                                        onChange={(e) => updateTool('grid', { size: e.value })}
                                        min={10}
                                        max={50}
                                        step={5}
                                        className="w-full"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 mb-2 block">{t('layouts.designer_grid_color')}</label>
                                    <ColorPicker
                                        value={toolsState.grid.color.replace('#', '')}
                                        onChange={(e) => updateTool('grid', { color: `#${e.value}` })}
                                        format="hex"
                                        className="w-full"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 mb-2 block">{t('layouts.designer_opacity_pct', { count: Math.round(toolsState.grid.opacity * 100) })}</label>
                                    <Slider
                                        value={toolsState.grid.opacity}
                                        onChange={(e) => updateTool('grid', { opacity: e.value })}
                                        min={0.1}
                                        max={1}
                                        step={0.1}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        </ToolSection>

                        <ToolSection
                            title={t('layouts.designer_snap_to_grid')}
                            icon="bx-magnet"
                            enabled={toolsState.grid.snapToGrid}
                            onToggle={() => updateTool('grid', { snapToGrid: !toolsState.grid.snapToGrid })}
                            onHelp={() => showToolTutorial('snapToGrid')}
                        >
                            <div className="text-xs text-gray-600 bg-amber-50 p-3 rounded-lg border border-amber-100">
                                <i className="bx bx-info-circle text-amber-600 mr-1"></i>
                                {t('layouts.designer_snap_to_grid_desc')}
                            </div>
                        </ToolSection>
                    </div>
                )}

                {/* Measure Tab */}
                {activeTab === 'measure' && (
                    <div className="space-y-4">
                        <ToolSection
                            title={t('layouts.designer_rulers')}
                            icon="bx-ruler"
                            enabled={toolsState.rulers.enabled}
                            onToggle={() => toggleTool('rulers')}
                            onHelp={() => showToolTutorial('rulers')}
                        >
                            <div>
                                <label className="text-xs font-semibold text-gray-600 mb-2 block">{t('layouts.designer_ruler_color')}</label>
                                <ColorPicker
                                    value={toolsState.rulers.color.replace('#', '')}
                                    onChange={(e) => updateTool('rulers', { color: `#${e.value}` })}
                                    format="hex"
                                    className="w-full"
                                />
                            </div>
                        </ToolSection>

                        <ToolSection
                            title={t('layouts.designer_alignment_guides')}
                            icon="bx-target-lock"
                            enabled={toolsState.alignmentGuides.enabled}
                            onToggle={() => toggleTool('alignmentGuides')}
                            onHelp={() => showToolTutorial('alignmentGuides')}
                        >
                            <div className="space-y-3">
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 mb-2 block">{t('layouts.designer_guide_color')}</label>
                                    <ColorPicker
                                        value={toolsState.alignmentGuides.color.replace('#', '')}
                                        onChange={(e) => updateTool('alignmentGuides', { color: `#${e.value}` })}
                                        format="hex"
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        </ToolSection>

                        <ToolSection
                            title={t('layouts.designer_safe_zones')}
                            icon="bx-shield"
                            enabled={toolsState.safeZones.enabled}
                            onToggle={() => toggleTool('safeZones')}
                            onHelp={() => showToolTutorial('safeZones')}
                        >
                            <div className="space-y-3">
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 mb-2 block">{t('layouts.designer_margin_pct', { count: toolsState.safeZones.margin })}</label>
                                    <Slider
                                        value={toolsState.safeZones.margin}
                                        onChange={(e) => updateTool('safeZones', { margin: e.value })}
                                        min={0}
                                        max={15}
                                        step={1}
                                        className="w-full"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 mb-2 block">{t('layouts.designer_zone_color')}</label>
                                    <ColorPicker
                                        value={toolsState.safeZones.color.replace('#', '')}
                                        onChange={(e) => updateTool('safeZones', { color: `#${e.value}` })}
                                        format="hex"
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        </ToolSection>
                    </div>
                )}

                {/* Colors Tab */}
                {activeTab === 'colors' && (
                    <div className="space-y-4">
                        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-4 rounded-xl border border-purple-100">
                            <div className="flex items-center gap-2 mb-3">
                                <i className="bx bx-palette text-purple-600 text-lg"></i>
                                <h4 className="text-sm font-bold text-gray-800 m-0">{t('layouts.designer_area_colors')}</h4>
                            </div>
                            <p className="text-xs text-gray-600 mb-3">{t('layouts.designer_area_colors_desc')}</p>

                            {areas.length === 0 ? (
                                <div className="text-xs text-gray-500 text-center py-4 bg-white rounded-lg">
                                    {t('layouts.designer_no_areas')}
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {areas.map((area, index) => (
                                        <div key={area.id} className="bg-white p-3 rounded-lg border border-gray-100 hover:border-purple-200 transition-colors">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-xs font-semibold text-gray-700">
                                                    {area.name || `Area ${index + 1}`}
                                                </span>
                                                <button
                                                    onClick={() => showToolTutorial('colorPicker')}
                                                    className="text-gray-400 hover:text-purple-600 transition-colors"
                                                >
                                                    <i className="bx bx-help-circle text-sm"></i>
                                                </button>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <ColorPicker
                                                    value={(toolsState.areaColors[area.id] || '#8b5cf6').replace('#', '')}
                                                    onChange={(e) => setAreaColor(area.id, `#${e.value}`)}
                                                    format="hex"
                                                />
                                                <div className="flex-1">
                                                    <label className="text-[10px] text-gray-500 mb-1 block">{t('layouts.designer_opacity_pct', { count: Math.round((toolsState.areaOpacity[area.id] || 0.4) * 100) })}</label>
                                                    <Slider
                                                        value={toolsState.areaOpacity[area.id] || 0.4}
                                                        onChange={(e) => setAreaOpacity(area.id, e.value)}
                                                        min={0.1}
                                                        max={1}
                                                        step={0.1}
                                                        className="w-full"
                                                        pt={{
                                                            root: { className: 'h-1' }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className={`p-3 border-t shrink-0 transition-colors ${
                isDarkMode ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-100'
            }`}>
                <div className={`text-[10px] text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    <kbd className={`px-2 py-1 border rounded font-mono ${
                        isDarkMode ? 'bg-slate-800 border-slate-700 text-gray-400' : 'bg-white border-gray-200 text-gray-700'
                    }`}>Ctrl+Shift+T</kbd>
                    <span className="mx-1">{t('layouts.designer_toggle_panel')}</span>
                </div>
            </div>
        </div>
    );
};

// Tool Section Component
const ToolSection = ({ title, icon, enabled, onToggle, onHelp, children, isDarkMode }) => {
    return (
        <div className={`border rounded-xl p-4 transition-colors ${
            isDarkMode 
            ? 'bg-slate-900 border-slate-700 hover:border-indigo-900' 
            : 'bg-white border-gray-200 hover:border-indigo-200'
        }`}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <i className={`bx ${icon} text-lg ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}></i>
                    <h4 className={`text-sm font-bold m-0 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{title}</h4>
                </div>
                <div className="flex items-center gap-2">
                    {onHelp && (
                        <button
                            onClick={onHelp}
                            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${
                                isDarkMode 
                                ? 'hover:bg-indigo-900/50 text-slate-500 hover:text-indigo-400' 
                                : 'hover:bg-indigo-50 text-gray-400 hover:text-indigo-600'
                            }`}
                            title="Show Tutorial"
                        >
                            <i className="bx bx-help-circle text-base"></i>
                        </button>
                    )}
                    {onToggle && (
                        <InputSwitch
                            checked={enabled}
                            onChange={onToggle}
                            className="scale-75"
                            pt={{
                                slider: ({ props }) => ({
                                    className: props.checked ? 'bg-indigo-600' : (isDarkMode ? 'bg-slate-700' : 'bg-gray-300')
                                })
                            }}
                        />
                    )}
                </div>
            </div>
            {children && <div className="mt-3">{children}</div>}
        </div>
    );
};

export default DesignToolsPanel;
